import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "../lib/config";
import { useLang } from "../i18n/LanguageContext";

const pad = (n) => String(n).padStart(2, "0");
const key = (y, m) => `${y}-${pad(m)}`;

export const Calendar = ({ tripId, selected, onSelect, refreshKey }) => {
  const { t, lang } = useLang();
  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() + 1 });
  const [cache, setCache] = useState({});
  const k = key(view.y, view.m);
  const days = cache[k];

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/trips/${tripId}/availability`, { params: { year: view.y, month: view.m } })
      .then((r) => alive && setCache((c) => ({ ...c, [k]: r.data })))
      .catch(() => {});
    return () => { alive = false; };
  }, [tripId, view.y, view.m, k, refreshKey]);

  const weekdays = useMemo(() => {
    const f = new Intl.DateTimeFormat(lang, { weekday: "short" });
    return [1, 2, 3, 4, 5, 6, 7].map((d) => f.format(new Date(2024, 0, d)).slice(0, 2));
  }, [lang]);
  const monthLabel = new Intl.DateTimeFormat(lang, { month: "long", year: "numeric" }).format(new Date(view.y, view.m - 1, 1));
  const offset = (new Date(view.y, view.m - 1, 1).getDay() + 6) % 7;
  const isCurrent = view.y === today.getFullYear() && view.m === today.getMonth() + 1;

  const move = (d) => setView((v) => {
    const dt = new Date(v.y, v.m - 1 + d, 1);
    return { y: dt.getFullYear(), m: dt.getMonth() + 1 };
  });

  return (
    <div data-testid={`calendar-${tripId}`}>
      <div className="mb-3 flex items-center justify-between">
        <button type="button" onClick={() => move(-1)} disabled={isCurrent} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-30" data-testid={`cal-prev-${tripId}`} aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
        <span className="font-display text-sm font-bold capitalize text-[var(--ink)]" data-testid={`cal-month-${tripId}`}>{monthLabel}</span>
        <button type="button" onClick={() => move(1)} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100" data-testid={`cal-next-${tripId}`} aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {weekdays.map((w) => <span key={w}>{w}</span>)}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {Array.from({ length: offset }).map((_, i) => <span key={`e${i}`} />)}
        {!days && Array.from({ length: 30 }).map((_, i) => <span key={`s${i}`} className="h-10 animate-pulse rounded-xl bg-slate-100" />)}
        {days && days.map((d) => {
          const n = Number(d.date.slice(-2));
          const sel = d.date === selected;
          const disabled = d.status === "past" || d.status === "full";
          return (
            <button
              key={d.date}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(d.date, d.spots_left)}
              className={`day day-${d.status} ${sel ? "day-selected" : ""}`}
              data-testid={`day-${tripId}-${d.date}`}
              data-status={d.status}
              aria-label={`${d.date} ${d.status}`}
            >
              {n}
              {d.status === "few" && !sel && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-amber-500" />}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-medium text-slate-500">
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-slate-200" />{t.trips.free}</span>
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-amber-200" />{t.trips.few}</span>
        <span className="flex items-center gap-1.5"><i className="day-full h-2.5 w-2.5 rounded-sm" />{t.trips.full}</span>
      </div>
    </div>
  );
};
